// Top-ups asked for at a PC (F-KIO-10, spec/12 §7.1), in the Floor's "What needs you" (spec/13 §3.2) — the desk's
// list, moved here from the Wallet screen when that folded into Players (2026-10-07), so staff see a waiting player
// where they already look. A player chose "Pay at the desk" for a wallet top-up at the kiosk; nothing is credited
// until staff take the money here. **Take payment** records the counter tender and tops the wallet up through the
// ordinary counter top-up (server-side, through the ledger, audited, with the offer's bonus); **Cancel** closes the
// request with nothing credited. The list refreshes on its own — the kiosk is waiting on it. `wallet:topup` gates it,
// like a counter top-up; the section is not drawn without it, nor when nothing waits.

import type { KioskTopUpRequest, KioskTopUpRequestTender } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Select, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useProblem } from '../api/use-problem';
import { useCancelKioskTopUp, useCompleteKioskTopUp, useKioskTopUps } from '../org/wallet/queries';
import { Notice } from '../ui';

const TENDERS: readonly KioskTopUpRequestTender[] = ['cash', 'counter_card', 'counter_upi'];

export function KioskTopUps({ organizationId, venueId, enabled }: { readonly organizationId: string; readonly venueId: string; readonly enabled: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const requests = useKioskTopUps(organizationId, venueId, enabled);
  const waiting = enabled ? (requests.data ?? []) : [];
  if (waiting.length === 0) return null;
  return (
    <section className="console-floor__panel-section" aria-labelledby="console-floor-topups">
      <h3 className="console-floor__panel-subtitle" id="console-floor-topups">
        <span>{t('wallet.kiosk.title')}</span>
        <Badge tone="warn">{waiting.length}</Badge>
      </h3>
      <ul className="console-kiosk-topups">
        {waiting.map((request) => (
          <KioskTopUpRow key={request.id} organizationId={organizationId} venueId={venueId} request={request} />
        ))}
      </ul>
    </section>
  );
}

/** How many top-ups wait on the desk, for the panel's count; none without `wallet:topup`. */
export function useKioskTopUpCount(organizationId: string, venueId: string, enabled: boolean): number {
  const requests = useKioskTopUps(organizationId, venueId, enabled);
  return enabled ? (requests.data?.length ?? 0) : 0;
}

function KioskTopUpRow({ organizationId, venueId, request }: { readonly organizationId: string; readonly venueId: string; readonly request: KioskTopUpRequest }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const problem = useProblem();
  const { show } = useToast();
  const complete = useCompleteKioskTopUp(organizationId, venueId);
  const cancel = useCancelKioskTopUp(organizationId, venueId);
  // "Scan & pay (UPI)" (F-PAY-02): the player paid by the venue's own QR at the PC — confirmed with counter UPI.
  const [tender, setTender] = useState<KioskTopUpRequestTender>(request.scanToPayReference !== undefined ? 'counter_upi' : 'cash');
  const [error, setError] = useState<string | null>(null);
  // Cancel closes the request with nothing credited and cannot be undone — a player is waiting at the PC — so it
  // confirms in place first (design guide §4), rather than firing on a stray click.
  const [confirming, setConfirming] = useState(false);
  const who = request.playerName ?? t('wallet.kiosk.unnamed');
  // Why the counter could not take it: the shared copy for the server's code (no open drawer, wallets off, a closed
  // request, a limit), and this screen's own line only for a code with no copy.
  const fail = (mutationError: unknown): void => setError(problem.text(mutationError, { fallback: t('wallet.kiosk.failed') }));

  return (
    <li className="console-kiosk-topups__row">
      <div className="console-kiosk-topups__who">
        <span className="console-kiosk-topups__line">
          <strong>{who}</strong>
          <span className="console-kiosk-topups__amount console-num">
            {format.money(request.amount)}
            {request.bonus.amount > 0 ? <span className="console-subtle"> {t('wallet.kiosk.bonus', { bonus: format.money(request.bonus) })}</span> : null}
          </span>
        </span>
        <span className="console-subtle">{t('wallet.kiosk.at', { station: request.stationName, time: format.time(request.createdAt) })}</span>
        {request.scanToPayReference !== undefined ? (
          <span className="console-kiosk-topups__scan">
            <Badge tone="accent">{t('wallet.kiosk.scanBadge')}</Badge>{' '}
            <span className="console-num">{t('wallet.kiosk.scanReference', { reference: request.scanToPayReference })}</span>
          </span>
        ) : null}
      </div>
      {confirming ? (
        <div className="console-kiosk-topups__confirm" role="group" aria-label={t('wallet.kiosk.cancelLabel', { who })}>
          <span className="console-subtle">{t('wallet.kiosk.cancelConfirm', { who })}</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            {t('wallet.kiosk.keep')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            busy={cancel.isPending}
            onClick={() => {
              setError(null);
              cancel.mutate(request.id, { onError: fail, onSettled: () => setConfirming(false) });
            }}
          >
            {t('wallet.kiosk.cancelConfirmAction')}
          </Button>
        </div>
      ) : (
        <div className="console-kiosk-topups__actions">
          <Select
            className="console-kiosk-topups__tender"
            aria-label={t('wallet.kiosk.tender')}
            value={tender}
            onValueChange={(value) => setTender(value as KioskTopUpRequestTender)}
            options={TENDERS.map((value) => ({ value, label: t(`wallet.tender.${value}`) }))}
          />
          <Button
            variant="primary"
            size="sm"
            busy={complete.isPending}
            onClick={() => {
              setError(null);
              complete.mutate({ requestId: request.id, tender }, { onSuccess: () => show({ tone: 'ok', title: t('wallet.kiosk.taken', { who }) }), onError: fail });
            }}
          >
            {t('wallet.kiosk.take')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('wallet.kiosk.cancelLabel', { who })}
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
          >
            {t('wallet.kiosk.cancel')}
          </Button>
        </div>
      )}
      {error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </li>
  );
}
